import React, { memo, useEffect, useMemo, useState } from "react";
import styles from "./IdentityInformation.module.scss";
import FormWrapper from "../../../../common/components/form-wrapper/FormWrapper";
import CommonFormFieldWrapper, { getFormInputErrors } from "../../../../common/components/form-field-wrapper/CommonFormFieldWrapper";
import CommonFileUploadInput, { StyleOfFileInput } from "../../../../common/components/file-upload-input/CommonFileUploadInput";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../../stores/store";
import { useForm, Controller } from "react-hook-form";
import CommonSelect, { CommonSelectItem } from "../../../../common/components/select/CommonSelect";
import { updateIdentityInformation } from "../../../../stores/slices/customer-registration-slice";
import { toast } from "react-toastify";
import { FileFromObjectUrl } from "../../../../model/common/file-from-object-url";
import CustomerSignUpFormWrapper from "../../../sign-up/wrapper/CustomerSignUpFormWrapper";
import Input from "../../../../common/components/new/ui/Input";
import { CardIcon, CardOrangeIcon, ImageIcon, ProfileIcon } from "../../../../assets/icons";
import Select from "../../../../common/components/new/select/Select";
import FileUploadInput from "../../../../common/components/new/file-upload/FileUploadInput";
import { hideLoader, showLoader } from "../../../../stores/slices/loader-slice";

type Props = {
	stepNumber: number
    onBack: (index: number) => void
    onNext: (index: number) => void
};

type IdentityFormData = {
	identityPhoto?: FileFromObjectUrl;
	identityType?: CommonSelectItem;
	identityNumber?: string;
};

const IdentityInformation: React.FC<Props> = memo((props: Props) => {
	const dispatch = useDispatch<AppDispatch>();
	
	const { identityInformation, loading } = useSelector((state: RootState) => state.customerRegistration);

	const {
		register,
		handleSubmit,
		trigger,
		setValue,
		watch,
		getValues,
		control,
		formState: { errors, isValid },
	} = useForm<IdentityFormData>({
		reValidateMode: "onSubmit",
		mode: "onBlur",
	});

	const identityTypes = useMemo(() => [
		new CommonSelectItem("International Passport", "International Passport"), 
		new CommonSelectItem("NIN", "NIN")
	], []);

	useEffect(() => {
		if (identityInformation) {
			setValue("identityType", identityTypes.find(x => x.key === identityInformation.identificationType) ?? identityTypes[0]);
			setValue("identityNumber", identityInformation.identificationNumber);
			setValue("identityPhoto", identityInformation.photoFile);
		}
	}, [identityInformation, setValue, identityTypes]);

	
	const initialIdentityProof = () => {
        return watch('identityPhoto') !== undefined ? [watch('identityPhoto')!.name] : []
    }

    const onFilesSelection = (file: File[]) => {
        const files: FileFromObjectUrl[] = []

        file.forEach((f: File) => {
            files.push({
                name: f.name,
                url: URL.createObjectURL(f),
            })
        })

        setValue('identityPhoto', files[0], { shouldValidate: true, shouldTouch: true, shouldDirty: true })
    }
	

	const onSubmit = async () => {
		dispatch(showLoader())
		const data = getValues();

		console.log(data)

		const result = await dispatch(
			updateIdentityInformation({
				identificationType: data.identityType?.key,
				identificationNumber: data.identityNumber!,
				photoFile: data.identityPhoto
			})
		);
		if (updateIdentityInformation.fulfilled.match(result)) {
			toast.success("Identity information saved successfully");
			props.onNext(props.stepNumber + 1)
		} else if (updateIdentityInformation.rejected.match(result)) {
			toast.error(result.payload as string);
		}
		dispatch(hideLoader())
	};

	const registeredInputs = useMemo(() => {
		return {
			identityType: register("identityType", { required: "Identity type is required" }),
			identityPhoto: register("identityPhoto", { required: "identity photo is required" }),
            identityNumber: register("identityNumber", { required: "identity number is required" }),
		};
	}, [register, watch]);

	return (
		<CustomerSignUpFormWrapper
			onBack={() => props.onBack(props.stepNumber - 1)}
			onNext={onSubmit}
			nextButtonDisabled={!isValid}
			labelBackStep="Employment"
			labelNextStep="Financial"
			title='Verification Information'
			subtitle='Verify Identity Information'
		>
			<div className={styles.container}>
				<Select
					label="Select Preferred ID type"
					placeholder="Select"
					onChange={value => setValue('identityType', value)}
					required
					error={errors.identityType?.message}
					items={identityTypes}
				>
					<ImageIcon />
                </Select>
				<FileUploadInput
                    label='Upload Identification Document'
                    maxFileSize={24}
                    accept='image/*'
					required
                    error={errors.identityPhoto?.message}
                    styleOfInput={StyleOfFileInput.FormInput}
                    registerInputProps={registeredInputs.identityPhoto}
                    onFilesSelect={onFilesSelection}
                >
					<CardOrangeIcon />
				</FileUploadInput>
				<Input
					ref={registeredInputs.identityNumber.ref}
					label="Add Your ID Number"
					placeholder="1234 567 8901"
					required
					error={errors.identityNumber?.message}
					inputProps={registeredInputs.identityNumber}
				>
					<ProfileIcon />
				</Input>
			</div>
		</CustomerSignUpFormWrapper>
	);
});

export default IdentityInformation;
